import React from 'react';
import { GridContainer, WideGridContainer } from '../../../../../../shared/form/FormFieldRow';
import { VerticalCheckboxField } from '../../../../../../shared/form/inputs/CheckboxField';
import { SelectField } from '../../../../../../shared/form/inputs/SelectField';
import { TextInputField } from '../../../../../../shared/form/inputs/TextInputField';
import { floorLevelSelectOptions } from '../../../quoteOrDesignFormData';
import { roomFieldName } from '../../../quoteOrDesignFormFieldNameHelpers';
import { RoomFormModel } from '../../roomFormModel';
import { RoomAutocompleteItem, RoomType, roomTypeSelectOptions } from '../../roomTypes';
import { RoomFloorConstructionField } from '../RoomFloorConstructionField';
import { RoomNameField } from '../RoomNameField';
import { RoomPerimeterField } from '../RoomPerimeterField';

// The Basic fields for a Room are:
// * Floor Level
// * Room Name
// * Temperature
// * Floor Construction
// * Total Area (i.e. Area)
// * Unheated Area
// * Void Area
// * Manifold Distance
// * Vertical Runback
// * Override Heat Loss

type Props = {
  unitIndex: number;
  manifoldIndex: number;
  roomIndex: number;
  isWithinRadiatorManifold: boolean;
  room: RoomFormModel;
  handleSelectionOfRoomName: (selectedItem: RoomAutocompleteItem) => void;
  handleSelectionOfRoomType: (selectedType: RoomType) => void;
};

export const RoomFieldsBasic = ({
  unitIndex,
  manifoldIndex,
  roomIndex,
  isWithinRadiatorManifold,
  room,
  handleSelectionOfRoomName,
  handleSelectionOfRoomType,
}: Props) => (
  <>
    <GridContainer>
      <SelectField
        name={roomFieldName(unitIndex, manifoldIndex, roomIndex, 'floorLevelCode')}
        label="Level"
        options={floorLevelSelectOptions}
      />
    </GridContainer>
    <WideGridContainer>
      <RoomNameField
        unitIndex={unitIndex}
        manifoldIndex={manifoldIndex}
        roomIndex={roomIndex}
        handleSelectionOfRoomName={handleSelectionOfRoomName}
      />
    </WideGridContainer>
    <WideGridContainer>
      <SelectField
        name={roomFieldName(unitIndex, manifoldIndex, roomIndex, 'roomType')}
        label="Room type"
        options={roomTypeSelectOptions}
        onChange={handleSelectionOfRoomType}
      />
    </WideGridContainer>
    <GridContainer>
      <TextInputField name={roomFieldName(unitIndex, manifoldIndex, roomIndex, 'roomTemperature')} label="Temp. (°C)" />
    </GridContainer>
    <WideGridContainer>
      <RoomFloorConstructionField
        unitIndex={unitIndex}
        manifoldIndex={manifoldIndex}
        roomIndex={roomIndex}
        isWithinRadiatorManifold={isWithinRadiatorManifold}
      />
    </WideGridContainer>
    <GridContainer>
      <RoomPerimeterField unitIndex={unitIndex} manifoldIndex={manifoldIndex} roomIndex={roomIndex} room={room} />
    </GridContainer>
    <GridContainer>
      <TextInputField
        name={roomFieldName(unitIndex, manifoldIndex, roomIndex, 'areaInMetresSquared')}
        label={
          <>
            Total Area (m<sup>2</sup>)
          </>
        }
      />
    </GridContainer>
    <GridContainer>
      <TextInputField
        name={roomFieldName(unitIndex, manifoldIndex, roomIndex, 'designRoomDetails.unheatedAreaInMetresSquared')}
        label={
          <>
            Unheated Area (m<sup>2</sup>)
          </>
        }
      />
    </GridContainer>
    <GridContainer>
      <TextInputField
        name={roomFieldName(unitIndex, manifoldIndex, roomIndex, 'designRoomDetails.voidAreaInMetresSquared')}
        label={
          <>
            Void Area (m<sup>2</sup>)
          </>
        }
      />
    </GridContainer>
    <GridContainer>
      <TextInputField
        name={roomFieldName(unitIndex, manifoldIndex, roomIndex, 'designRoomDetails.manifoldDistanceInMetres')}
        label="Manifold Distance (m)"
      />
    </GridContainer>
    <GridContainer>
      <TextInputField
        name={roomFieldName(unitIndex, manifoldIndex, roomIndex, 'designRoomDetails.verticalRunbackInMetres')}
        label="Vertical Runback (m)"
      />
    </GridContainer>
    <GridContainer>
      <VerticalCheckboxField
        name={roomFieldName(unitIndex, manifoldIndex, roomIndex, 'heatLossDetails.overrideTotalHeatLossInWatts')}
        label="Override Heat Loss?"
      />
    </GridContainer>
  </>
);
